import {ChangeDetectorRef, Component, ElementRef, Inject, OnInit} from '@angular/core';
import {
    LEARNING_PROGRESS_INSIGHT,
    LearningProgressInsightService
} from '../../../../services/LearningProgressInsightService';
import {InsightPopupService} from '../insight-popup.service';
import {Observable} from 'rxjs';
import {filter, switchMap} from 'rxjs/operators';
import {
    LearningPackageInsightsQuestionsResponseDto
} from '../../../../services/rest/api-types/LearningPackageInsightsQuestionsResponseDto';
import {CssColorsGetter} from '../CssColorsGetter';

@Component({
    selector: 'mm-questions-insight-popup',
    templateUrl: './questions-insight-popup.component.html',
    styleUrl: './questions-insight-popup.component.scss',
    standalone: false
})
export class QuestionsInsightPopupComponent implements OnInit {

    constructor(public insightPopupService: InsightPopupService,
                @Inject(LEARNING_PROGRESS_INSIGHT) private learningProgressInsightService: LearningProgressInsightService,
                private host: ElementRef<HTMLElement>,
                private changeDetectorRef: ChangeDetectorRef) {
    }

    public questionsOverview$: Observable<LearningPackageInsightsQuestionsResponseDto> = this.insightPopupService.selectedLearningPackageId$.pipe(
        filter((learningPackageId): learningPackageId is string => learningPackageId !== undefined),
        switchMap((learningPackageId: string) => this.learningProgressInsightService.getLearningProgressInsightQuestionsOverview(learningPackageId))
    );

    public size = 250;
    public strokeWidth = 1.5;
    public answeredQuestions!: number;
    public numberOfAvailableQuestion!: number;
    public totalElements!: number;
    public elementsPerLine!: number;
    public squareWidth!: number;

    public levels!: number[];

    public levels1 = Array.from({length: 20}, (_, i) => i + 1);
    public colors = this.levels1.map(level => {
        const hue = (level / this.levels1.length) * 120;
        return `hsl(${hue}, 75%, 50%)`;
    });

    public ngOnInit(): void {
        this.questionsOverview$.subscribe(questionsOverview => {
            this.levels = questionsOverview.levels;
            this.answeredQuestions = this.calculateAllElements(this.levels);
            this.numberOfAvailableQuestion = this.answeredQuestions + questionsOverview.neverAnsweredQuestions;
            this.totalElements = this.numberOfAvailableQuestion + questionsOverview.numberOfInactiveQuestions;
            this.elementsPerLine = this.calculateNextSquareNumber(this.totalElements);
            this.squareWidth = this.calculateSquareWidth(
                this.size,
                this.elementsPerLine,
                this.strokeWidth
            );
            this.changeDetectorRef.markForCheck();
        })
    }

    public calculateNextSquareNumber(n: number): number {
        return Math.floor(Math.sqrt(n)) + 1;
    }

    public calculateSquareWidth(
        fullSize: number,
        elementsPerLine: number,
        strokeWidth: number
    ): number {
        const realStrokeWidth = strokeWidth / 2;
        return (
            (fullSize - elementsPerLine * realStrokeWidth - realStrokeWidth) / elementsPerLine
        );
    }

    public calculateAllElements(levels: number[]): number {
        return levels.reduce((a, b) => a + b, 0);
    }

    public calculateXPosition(index: number): number {
        return this.strokeWidth / 2 + (this.squareWidth + this.strokeWidth / 2) * (index % this.elementsPerLine);
    }

    public calculateYPosition(index: number): number {
        return (
            this.strokeWidth / 2 + (this.squareWidth + this.strokeWidth / 2) *
            ((index - (index % this.elementsPerLine)) / this.elementsPerLine)
        );
    }

    public calculateBackgroundColor(index: number): string {
        if (this.isForInactiveQuestion(index)) {
            return CssColorsGetter.get(this.host.nativeElement, '--inactive');
        }
        const level = this.calculateLevel(index);
        switch (level) {
            case 0:
                return 'transparent';
            case -1:
                return CssColorsGetter.get(this.host.nativeElement, '--not-answered');
            default:
                return `hsl(${((level / this.levels.length) * 120)}, 75%, 50%)`;
        }
    }

    private isForInactiveQuestion(index: number): boolean {
        return index >= this.numberOfAvailableQuestion && index < this.totalElements;
    }

    public calculateLevel(index: number): number {
        if (index + 1 > this.answeredQuestions) {
            return index + 1 > this.totalElements ? 0 : -1;
        }
        let i = 0;
        let rest = -1 * (index + 1);
        do {
            rest = this.levels[i] + rest;
            i++;
        } while (rest < 0);
        return i;
    }

}
